import { call, put } from 'redux-saga/effects';
import { logOut, genericError } from 'containers/App/actions';
import { get } from 'lodash';
import {
  ERROR_TYPE_QUESTIONNAIRE_VERSION,
  ERROR_TYPE_QUESTIONNAIRE_AND_TARGETING_VERSIONS,
} from 'containers/App/constants';
import Raven from 'raven-js';

/**
 * A generic http error hanlder
 * @param callAction the action to be called
 * @param httpReqOptions an object which should have the below structure:
 * {
 *    params: Params that we want to give on the action to be called,
 *    options: Options that we want to give on the action to be called,
 *    excludedStatuses: Array of excluded error statuses
 *    along with error messages examples of valid values:
 *    excludeFormValidation: boolean => exclude from generic handling
 *      all server form validation errors
 *    excludedStatuses: [400] => exclude from generic handling all 400 error status
 *    excludedStatuses: [{status: 400}] => exclude from generic handling all 400 error status
 *    excludedStatuses: [{status: 400, errors: ['TEST']}] =? exclude from generic handling
 *        only 400 error status with error returned: TEST
 * }
 * @returns {*}
 */
// eslint-disable-next-line
export function* httpRequest(callAction, httpReqOptions = {}) {
  if (!callAction) {
    yield put(genericError());
  } else {
    try {
      const result = yield call(
        callAction,
        get(httpReqOptions, 'params', {}),
        get(httpReqOptions, 'options', {}),
        get(httpReqOptions, 'body', {}),
      );
      return result;
    } catch (error) {
      const excludedStatuses = get(httpReqOptions, 'excludedStatuses', []);
      const excludeFormValidation = get(
        httpReqOptions,
        'excludeFormValidation',
        false,
      );
      const excludedError =
        (excludedStatuses.length &&
          excludedStatuses.find(
            (excluded) =>
              excluded === error.status || excluded.status === error.status,
          )) ||
        undefined;

      const bodyErrors = get(error, 'body.errors', []);
      const shouldThrowError =
        excludedError &&
        (!excludedError.errors ||
          excludedError.errors.length === 0 ||
          excludedError.errors.some((err) => bodyErrors.includes(err)));

      const shouldThrowValidationError =
        excludeFormValidation && get(error, 'body.form.validations', false);

      if (shouldThrowError || shouldThrowValidationError) {
        reportToSentry(error, {
          httpReqOptions,
        });
        throw error;
      } else if (error.status === 401 || error.status === 403) {
        yield put(logOut());
      } else {
        reportToSentry(error, {
          httpReqOptions,
        });
        // we want to stop execution here, thats why we don't throw an error
        console.error(error); // eslint-disable-line
        yield put(genericError(error));
      }
    }
  }
}

function reportToSentry(error, { httpReqOptions }) {
  if (process.env.NODE_ENV === 'production') {
    if (error.status === 400) {
      const errorBody = get(error, 'body.errors[0]');
      if (
        errorBody === ERROR_TYPE_QUESTIONNAIRE_VERSION ||
        errorBody === ERROR_TYPE_QUESTIONNAIRE_AND_TARGETING_VERSIONS
      ) {
        Raven.captureMessage('VERSION_MISSMATCH', {
          extra: { httpReqOptions, error },
        });
      }
    }
    const statusCode = get(error, 'status', null);
    const errorText = `HTTP-Error: ${statusCode}`;
    const requestURL = get(error, 'url', null);
    Raven.captureException(errorText, {
      fingerprint: [statusCode],
      tags: {
        requestURL,
      },
      extra: {
        httpReqOptions,
        error,
      },
    });
  }
}
